import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmailDeliveryModal = ({ invoice, onConfirm, onClose }) => {
  const [recipientEmail, setRecipientEmail] = useState(invoice?.customerEmail || '');
  const [ccEmails, setCcEmails] = useState('');
  const [subject, setSubject] = useState(`Invoice ${invoice?.invoiceNumber} from Business Jadhak`);
  const [message, setMessage] = useState(
    `Dear ${invoice?.customerName},\n\nPlease find attached invoice ${invoice?.invoiceNumber} for your recent purchase.\n\nTotal Amount: ₹${invoice?.totalAmount?.toLocaleString()}\nDue Date: ${new Date(invoice?.dueDate)?.toLocaleDateString()}\n\nThank you for your business!\n\nBest regards,\nBusiness Jadhak Team`
  );
  const [attachPDF, setAttachPDF] = useState(true);
  const [sendCopy, setSendCopy] = useState(false);

  const handleSend = () => {
    onConfirm({
      invoiceId: invoice?.id,
      recipientEmail,
      ccEmails: ccEmails?.split(',')?.map(e => e?.trim())?.filter(e => e),
      subject,
      message,
      attachPDF,
      sendCopy
    });
  };

  const canSend = recipientEmail && subject && message;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Icon name="Mail" size={24} color="#4ADE80" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white font-heading">Send Invoice via Email</h2>
              <p className="text-sm text-slate-400 font-body">{invoice?.invoiceNumber}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-700 transition-colors"
          >
            <Icon name="X" size={20} color="#94A3B8" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Invoice Summary */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-slate-400 font-caption">Customer</p>
                <p className="text-sm text-white font-medium mt-1 font-body">{invoice?.customerName}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-caption">Amount</p>
                <p className="text-sm text-white font-medium mt-1 font-body">₹{invoice?.totalAmount?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-caption">Due Date</p>
                <p className="text-sm text-white font-medium mt-1 font-body">{new Date(invoice?.dueDate)?.toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          {/* Recipient Email */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2 font-body">
              Recipient Email *
            </label>
            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e?.target?.value)}
              placeholder="customer@example.com"
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          {/* CC Emails */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2 font-body">
              CC (Optional)
            </label>
            <input
              type="text"
              value={ccEmails}
              onChange={(e) => setCcEmails(e?.target?.value)}
              placeholder="email1@example.com, email2@example.com"
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <p className="text-xs text-slate-400 mt-1 font-caption">Separate multiple emails with commas</p>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2 font-body">
              Subject *
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e?.target?.value)}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2 font-body">
              Message *
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e?.target?.value)}
              rows={8}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none font-mono text-sm"
            />
          </div>

          {/* Options */}
          <div className="space-y-3">
            <label className="flex items-center space-x-3 p-3 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors">
              <input
                type="checkbox"
                checked={attachPDF}
                onChange={(e) => setAttachPDF(e?.target?.checked)}
                className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500"
              />
              <div className="flex items-center space-x-2">
                <Icon name="Paperclip" size={18} color="#94A3B8" />
                <div>
                  <p className="text-sm font-medium text-white font-body">Attach PDF Invoice</p>
                  <p className="text-xs text-slate-400 font-caption">Include invoice as PDF attachment</p>
                </div>
              </div>
            </label>
            <label className="flex items-center space-x-3 p-3 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors">
              <input
                type="checkbox"
                checked={sendCopy}
                onChange={(e) => setSendCopy(e?.target?.checked)}
                className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500"
              />
              <div className="flex items-center space-x-2">
                <Icon name="Copy" size={18} color="#94A3B8" />
                <div>
                  <p className="text-sm font-medium text-white font-body">Send me a copy</p>
                  <p className="text-xs text-slate-400 font-caption">Receive a copy of this email</p>
                </div>
              </div>
            </label>
          </div>

          {/* Info */}
          <div className="flex items-start space-x-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <Icon name="Info" size={20} color="#60A5FA" />
            <div>
              <p className="text-sm text-slate-300 font-body">
                The invoice will be sent immediately. Delivery tracking and bounce management are enabled.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-800 border-t border-slate-700 px-6 py-4 flex items-center justify-end space-x-3 rounded-b-xl">
          <Button onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!canSend}
            className="bg-green-500 hover:bg-green-600"
          >
            <Icon name="Send" size={18} />
            <span className="ml-2">Send Invoice</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailDeliveryModal;